'use client';

import { useEffect, useMemo, useState } from 'react';
import { Syne } from 'next/font/google';

type ComingSoonPageProps = {
  locale: string;
};

type CountdownState = {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
  done: boolean;
};

const launchDate = new Date('2026-04-26T12:00:00+02:00');
const syne = Syne({ subsets: ['latin'], weight: ['600', '700'] });
const grainBackground =
  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E\")";

function getCountdownState(now: Date): CountdownState {
  const ms = launchDate.getTime() - now.getTime();

  if (ms <= 0) {
    return { days: '0', hours: '0', minutes: '0', seconds: '0', done: true };
  }

  const totalSec = Math.floor(ms / 1000);
  const seconds = totalSec % 60;
  const minutes = Math.floor(totalSec / 60) % 60;
  const hours = Math.floor(totalSec / 3600) % 24;
  const days = Math.floor(totalSec / 86400);

  return {
    days: String(days),
    hours: String(hours),
    minutes: String(minutes),
    seconds: String(seconds),
    done: false,
  };
}

export function ComingSoonPage({ locale }: ComingSoonPageProps) {
  const [countdown, setCountdown] = useState<CountdownState>({
    days: '--',
    hours: '--',
    minutes: '--',
    seconds: '--',
    done: false,
  });
  const isFrench = locale === 'fr';

  const copy = useMemo(
    () =>
      isFrench
        ? {
            launchLabel: 'Official launch · April 26, 2026',
            intro: 'Lancement officiel le ',
            introDate: 'dimanche 26 avril 2026',
            introTime: '12 h',
            introSuffix: '(heure de Paris).',
            days: 'Jours',
            hours: 'Heures',
            minutes: 'Minutes',
            seconds: 'Secondes',
            done: 'C’est lancé ! Rendez-vous sur le site principal.',
          }
        : {
            launchLabel: 'Official launch · April 26, 2026',
            intro: 'Official launch on ',
            introDate: 'Sunday, April 26, 2026',
            introTime: '12:00 PM',
            introSuffix: '(Paris time).',
            days: 'Days',
            hours: 'Hours',
            minutes: 'Minutes',
            seconds: 'Seconds',
            done: 'We are live! Head to the main website.',
          },
    [isFrench]
  );

  useEffect(() => {
    setCountdown(getCountdownState(new Date()));

    const timer = window.setInterval(() => {
      setCountdown(getCountdownState(new Date()));
    }, 1000);

    return () => window.clearInterval(timer);
  }, []);

  return (
    <main className="relative flex min-h-[100dvh] flex-col overflow-hidden bg-[#161616] text-white">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(34,197,94,0.18),transparent_55%)]"
      />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{ backgroundImage: grainBackground }} />

      <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-5 py-16 text-center sm:py-20">
        <div className="flex w-full flex-col items-center gap-8 px-2 sm:gap-9">
          <div className="flex items-center gap-3 sm:gap-4">
            <img src="/pdf-logo-symbol.svg" alt="" className="h-14 w-14 sm:h-16 sm:w-16" />
            <span className={`${syne.className} text-3xl font-semibold tracking-[0.01em] text-white sm:text-4xl`}>MyPokerMetrics</span>
          </div>
          <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-white/50">{copy.launchLabel}</p>
        </div>

        <h1 className="mt-2 max-w-2xl select-none text-3xl font-extrabold leading-tight tracking-tight sm:mt-3 sm:text-4xl lg:text-5xl">
          <span className="block text-white">
            Poker is <span className="coming-soon-shimmer text-transparent">numbers</span>.
          </span>
          <span className="mt-2 block text-white">
            Know <span className="coming-soon-shimmer text-transparent">yours</span>.
          </span>
        </h1>

        <p className="mx-auto mt-5 max-w-md text-[17px] leading-relaxed text-white/60 sm:mt-6">
          {copy.intro}
          <strong className="font-semibold text-white">{copy.introDate}</strong> {' '}
          <strong className="font-semibold text-white">{copy.introTime}</strong> {copy.introSuffix}
        </p>

        {!countdown.done ? (
          <div
            className="mt-8 grid w-full max-w-lg grid-cols-2 gap-3 sm:mt-9 sm:grid-cols-4 sm:gap-4"
            role="timer"
            aria-live="polite"
            aria-atomic="true"
            aria-label={isFrench ? 'Temps restant avant le lancement' : 'Time left before launch'}
          >
            {[
              [countdown.days, copy.days],
              [countdown.hours, copy.hours],
              [countdown.minutes, copy.minutes],
              [countdown.seconds, copy.seconds],
            ].map(([value, label]) => (
              <div key={label} className="rounded-2xl border border-white/10 bg-white/5 px-4 py-5 backdrop-blur-sm">
                <p className="text-3xl font-extrabold tabular-nums text-[#22C55E] sm:text-4xl">{value}</p>
                <p className="mt-1 text-[12px] font-medium uppercase tracking-wide text-white/45">{label}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-8 max-w-md text-lg font-semibold text-[#22C55E] sm:mt-9">{copy.done}</p>
        )}
      </div>

      <footer className="relative z-10 border-t border-white/10 py-6 text-center text-[13px] text-white/40">
        © {new Date().getFullYear()} MyPokerMetrics
      </footer>

      <style jsx>{`
        .coming-soon-shimmer {
          background-image: linear-gradient(90deg, #22c55e, #86efac, #22c55e);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          animation: coming-soon-shimmer 8s ease-in-out infinite;
        }

        @keyframes coming-soon-shimmer {
          0%,
          100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .coming-soon-shimmer {
            animation: none !important;
          }
        }
      `}</style>
    </main>
  );
}
